import { Component, OnDestroy, OnInit, DestroyRef, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ActivatedRoute } from '@angular/router';
import { Fortigate } from '@app/shared/models/fortigate/fortigate.model';
import { Cradlepoint } from '@app/shared/models/cradlepoint/cradlepoint.model';
import { MerakiSwitchStatus } from '@app/shared/models/meraki/meraki-switch-status.model';
import { Mist } from '@app/shared/models/mist/mist.model';
import { FortigatesService } from '@app/features/fortigates/shared/fortigates.service';
import { CradlepointsService } from '@app/features/cradlepoints/shared/cradlepoints.service';
import { MerakisService } from '@app/features/merakis/shared/merakis.service';
import { MistService } from '@app/features/mists/shared/mist.service';
import { LocationsService } from '@app/features/locations/shared/locations.service';
import { CircuitService } from '@app/features/circuit/shared/circuit-service';
import { WanService } from '@app/features/wan/shared/wan.service';
import { Circuit } from '@app/shared/models/circuit.model';
import { FiberNid } from '@app/shared/models/fiber-nid.model';
import { environment } from '@env/environment';
import { firstValueFrom } from 'rxjs';
import {
  NetworkState,
  NetworkStateEvaluation,
  NETWORK_STATE,
  VPN_STATUS,
  WanInterfacesByHostname,
  evaluateNetworkState
} from '@app/shared/utils/network-thresholds';

@Component({
    selector: 'app-restaurant',
    templateUrl: './device.component.html',
    styleUrls: ['./device.component.css'],
    standalone: false
})
export class DeviceComponent implements OnInit, OnDestroy {

  expandedAps: Record<string, boolean> = {};
  allApsExpanded = false;

  isLoading: boolean;
  networkState: NetworkState;
  networkStateEvaluation: NetworkStateEvaluation;
  NETWORK_STATE = NETWORK_STATE;
  storeNumberInput: number;
  formattedStoreNumber: string;
  storeIsNotFound: boolean;
  fortigates: Fortigate[] = [];
  cradlepoints: Cradlepoint[] = [];
  merakiSwitchStatuses: MerakiSwitchStatus[] = [];
  mists: Mist[] = [];
  location: any[] = [];
  circuits: Circuit[] = [];
  restaurant = [];
  storeDashboardLinkFromLogicMonitor: string;
  storeDashboardLinkFromNetbox: string;
  mistSiteLink: string = null;
  merakiDashboardUrls: Record<string, Record<string, { url: string }>>;
  mostRecentRemodels: any[] = [];
  activeVpnStatuses = {};
  vpnDataAvailable = true;
  wanInterfacesByHostname: WanInterfacesByHostname = {};
  wanInterfaceDataAvailable = true;
  fiberNid: FiberNid;
  fiberNidDescription: string;
  circuitLocalLoop: string;
  circuitCommitRate: number;
  fiberNidImage = {};
  private destroyRef = inject(DestroyRef);

  constructor(
    private fortigatesService: FortigatesService,
    private cradlepointsService: CradlepointsService,
    private mearkisService: MerakisService,
    private mistService: MistService,
    private locationsService: LocationsService,
    private circuitService: CircuitService,
    private wanService: WanService,
    private activatedRoute: ActivatedRoute
  ) { 
    this.activatedRoute.queryParams.pipe(
      takeUntilDestroyed(this.destroyRef)
    ).subscribe(params => {
      if ('storeNumber' in params) {
        this.formattedStoreNumber = params['storeNumber'];
        this.storeNumberInput = Number(this.formattedStoreNumber);
        this.getRestaurantNetwork();
      }
    });
  }

  ngOnInit() { 
    this.formattedStoreNumber = localStorage.getItem('storeNumber');
  }

  prependZeros() {
    if (this.storeNumberInput) {
      this.formattedStoreNumber = this.storeNumberInput.toString();
      this.formattedStoreNumber = this.formattedStoreNumber.padStart(5, '0');
    }
    return this.formattedStoreNumber;
  }

  isStoreNumberInvalid() {
    if (this.storeNumberInput) {
      return this.storeNumberInput.toString().length > 5;
    } else {
      return true;
    }
  }

  ngOnDestroy() { }

  getSectionSeverity(type: string): 'green' | 'yellow' | 'red' | 'neutral' {
    switch (type) {
      case 'circuit':
        return this.circuits.every((c: Circuit) => c.loss === 0) ? 'green' : 'red';
      case 'fortigate':
        if (this.fortigates.some((f: Fortigate) => f.conn_status === 2)) { return 'red'; }
        if (this.fortigates.some((f: Fortigate) => f.conn_status === 0)) { return 'yellow'; }
        return 'green';
      case 'cradlepoint':
        return this.cradlepoints.every((c: Cradlepoint) => c.state === 'online') ? 'green' : 'red';
      case 'meraki':
        return this.merakiSwitchStatuses.every((m: MerakiSwitchStatus) => m.status === 'online') ? 'green' : 'red';
      case 'mist':
        return this.mists.every((m: Mist) => m.connected) ? 'green' : 'red';
      case 'fiberNid':
        return 'neutral';
      default:
        return 'neutral';
    }
  }

  getFortigateHealthSummary(): string {
    const online = this.fortigates.filter((f: Fortigate) => f.conn_status === 1).length;
    return `${online}/${this.fortigates.length} Online`;
  }

  getCradlepointHealthSummary(): string {
    const online = this.cradlepoints.filter((c: Cradlepoint) => c.state === 'online').length;
    return `${online}/${this.cradlepoints.length} Online`;
  }

  getMerakiHealthSummary(): string {
    const online = this.merakiSwitchStatuses.filter((m: MerakiSwitchStatus) => m.status === 'online').length;
    return `${online}/${this.merakiSwitchStatuses.length} Online`;
  }

  getMistHealthSummary(): string {
    const online = this.mists.filter((m: Mist) => m.connected).length;
    return `${online}/${this.mists.length} Online`;
  }

  toggleAp(mac: string) {
    this.expandedAps[mac] = !this.expandedAps[mac];
  }

  expandAllAps() {
    this.mists.forEach((m: Mist) => this.expandedAps[m.mac] = true);
    this.allApsExpanded = true;
  }

  collapseAllAps() {
    this.expandedAps = {};
    this.allApsExpanded = false;
  }

  formatUptime(seconds: number): string {
    if (!seconds || isNaN(seconds)) { return 'Unknown'; }
    const days = Math.floor(seconds / 86400);
    const hours = Math.floor((seconds % 86400) / 3600);
    const minutes = Math.floor(((seconds % 86400) % 3600) / 60);
    return `${days}d ${hours}h ${minutes}m`;
  }

  isAllHealthy(type: string): boolean {
    switch (type) {
      case 'circuit': return this.circuits.every((c: Circuit) => c.loss === 0);
      case 'fortigate': return this.fortigates.every((f: Fortigate) => f.conn_status === 1);
      case 'cradlepoint': return this.cradlepoints.every((c: Cradlepoint) => c.state === 'online');
      case 'meraki': return this.merakiSwitchStatuses.every((m: MerakiSwitchStatus) => m.status === 'online');
      case 'mist': return this.mists.every((m: Mist) => m.connected);
      default: return true;
    }
  }

  async getRestaurantNetwork() {
    this.prependZeros();
    localStorage.setItem('storeNumber', this.formattedStoreNumber);
    this.isLoading = true;
    this.resetView();

    try {
      await Promise.all(
        [
          this.getLocation(),
          this.getFortigates(),
          this.getCradlepoints(),
          this.getMerakiSwitcheStatuses(),
          this.getMists(),
          this.getCircuit(),
          this.getStoreDashboardLinkFromLogicMonitor(),
          this.getStoreDashboardLinkFromNetbox(),
          this.getMerakiDashboardUrls(),
          this.getActiveVpns(),
          this.getWanInterfaces(),
          this.getFiberNid(),
          this.getFiberNidImage(),
          this.getMistSiteLink()
        ]
      );
      this.setMerakiDashboardUrls();
      this.setMostRecentRemodel();
      this.combineFortigatesWithVpn();
      this.combineFortigatesWithWanInterfaces();
      this.networkStateEvaluation = evaluateNetworkState({
        fortigates: this.fortigates,
        cradlepoints: this.cradlepoints,
        merakiSwitchStatuses: this.merakiSwitchStatuses,
        wanInterfacesByHostname: this.wanInterfacesByHostname,
        vpnDataAvailable: this.vpnDataAvailable,
        wanInterfaceDataAvailable: this.wanInterfaceDataAvailable,
      });
      this.networkState = this.networkStateEvaluation.state;
      this.combineNidWithImage();
      this.isLoading = false;
    } catch (err) {
      console.log(err);
      console.log('Promise.all REJECTED');
      this.isLoading = false;
    }
  }

  async getLocation() {
    try {
      const response = await firstValueFrom(await this.locationsService.getLocationData(this.formattedStoreNumber));
      const locations_from_response = response?.['message'];
      locations_from_response["locationList"].forEach(locations_json => {
        let location = locations_json;
        this.location.push(location);
      });
      this.storeIsNotFound = false;
    } catch (error) {
      console.log("Locations timed out!");
      this.storeIsNotFound = true;
      throw error;
    }
  }

  async getFiberNid() {
    try {
      const response = await firstValueFrom(await this.circuitService.getNetboxCircuit(this.formattedStoreNumber));
      const circuit_response = response?.['message'];
      if (!circuit_response || typeof circuit_response === 'string') {
        return;
      }
      this.fiberNidDescription = circuit_response["nid_location"];
      this.circuitLocalLoop = circuit_response["local_loop"];
      this.circuitCommitRate = circuit_response["commit_rate"];
    } catch (error) {
      console.log("Fiber NID timed out!");
    }
  }

  async getFiberNidImage() {
    try {
      const response = await firstValueFrom(await this.locationsService.getFiberNidImage(this.formattedStoreNumber));
      const images_from_response = response?.['message'];
      this.fiberNidImage = this.pickNidImage(images_from_response);
    } catch (error) {
      console.log("Fiber NID images timed out!");
    }
  }

  pickNidImage(images: any) {
    if (!Array.isArray(images) || images.length === 0) {
      return {};
    }
    const expected = `${this.formattedStoreNumber}_nid`.toLowerCase();
    const named = images.find((image) => {
      const name = (image?.name || '').toLowerCase();
      const path = (image?.image || '').toLowerCase();
      return name.includes(expected) || path.includes(expected);
    });
    return named || images[0];
  }

  async getFortigates() {
    try {
      const response = await firstValueFrom(await this.fortigatesService.findByStoreNumberV2(this.formattedStoreNumber));
      const responseData = response?.['message'];
      if (responseData != null) {
        responseData.forEach((_fortigate: any) => {
          this.fortigates.push(_fortigate as Fortigate);
        });
      }
    } catch (error) {
      console.log("Fortigates timed out!");
    }
  }

  async getActiveVpns() {
    try {
      const response = await firstValueFrom(await this.fortigatesService.getActiveVpnStatuses(this.formattedStoreNumber));
      if (response?.["status"] != 200) {
        this.activeVpnStatuses = null;
        this.vpnDataAvailable = false;
        return;
      }
      this.activeVpnStatuses = response["message"] || {};
      // The endpoint lists hostnames it could not reach separately, so an empty
      // result only means "every tunnel is down" when that list is empty too.
      this.vpnDataAvailable = (response["unavailable"] || []).length === 0;
    } catch (error) {
      console.log("Get Active VPNs timed out!");
      this.activeVpnStatuses = null;
      this.vpnDataAvailable = false;
    }
  }

  async getWanInterfaces() {
    try {
      const response = await firstValueFrom(await this.fortigatesService.getWanInterfaces(this.formattedStoreNumber));
      if (response?.["status"] != 200) {
        this.wanInterfacesByHostname = {};
        this.wanInterfaceDataAvailable = false;
        return;
      }
      this.wanInterfacesByHostname = response["message"] || {};
      this.wanInterfaceDataAvailable = (response["unavailable"] || []).length === 0;
    } catch (error) {
      console.log("Get WAN interfaces timed out!");
      this.wanInterfacesByHostname = {};
      this.wanInterfaceDataAvailable = false;
    }
  }

  combineFortigatesWithVpn() {
    try {
      this.fortigates.forEach(fortigate => {
        if (this.activeVpnStatuses && fortigate.hostname in this.activeVpnStatuses) {
          fortigate.vpn_status = this.activeVpnStatuses[fortigate.hostname].name;
          // round to bytes to GB to nearest 100th
          fortigate.vpn_incoming_bytes = Math.round(this.activeVpnStatuses[fortigate.hostname].incoming_bytes / 10000000) / 100;
          fortigate.vpn_outgoing_bytes = Math.round(this.activeVpnStatuses[fortigate.hostname].outgoing_bytes / 10000000) / 100;
        } else {
          fortigate.vpn_status = VPN_STATUS.NOT_FOUND;
          fortigate.vpn_incoming_bytes = 0;
          fortigate.vpn_outgoing_bytes = 0;
        }
      });
    } catch (error) {
      console.log(error)
    }
  }

  // FortiManager's "ip" is only the address it uses to reach the device, which is a
  // tunnel address now that FortiManager sits behind the firewall, so the live wan1
  // address is what actually tells us how the store gets out to the internet.
  combineFortigatesWithWanInterfaces() {
    try {
      this.fortigates.forEach(fortigate => {
        const interfaces = this.wanInterfacesByHostname?.[fortigate.hostname];
        fortigate.wan1_ip = interfaces?.['wan1']?.ip || null;
        fortigate.wan1_link = interfaces?.['wan1']?.link ?? null;
        fortigate.wan2_ip = interfaces?.['wan2']?.ip || null;
        fortigate.wan2_link = interfaces?.['wan2']?.link ?? null;
      });
    } catch (error) {
      console.log(error)
    }
  }

  async getCradlepoints() {
    try {
      const response = await firstValueFrom(await this.cradlepointsService.findByStoreNumber(this.formattedStoreNumber));
      const cradlepoints_from_response = response?.['message'];
      cradlepoints_from_response.forEach((cradlepoint_json: any) => {
        let cradlepoint: Cradlepoint = cradlepoint_json;
        this.cradlepoints.push(cradlepoint);
      });
    } catch (error) {
      console.log("Cradlepoints timed out!");
    }
  }

  async getMerakiSwitcheStatuses() {
    try {
      const response = await firstValueFrom(await this.mearkisService.getSwitchStatusesLatent(this.formattedStoreNumber));
      const meraki_switch_statuses_from_response = response?.['message'];
      meraki_switch_statuses_from_response.forEach((meraki_switch_statuses_json: any) => {
        let meraki_switch_status: MerakiSwitchStatus = meraki_switch_statuses_json;
        this.merakiSwitchStatuses.push(meraki_switch_status);
      });
    } catch (error) {
      console.log("Meraki switch statuses timed out!");
    }
  }

  async getMists() {
    try {
      const mistsResponse = await firstValueFrom(await this.mistService.findByStoreNumber(this.formattedStoreNumber));
      const main_response_data = mistsResponse?.['message'];
      const detailsResponse = await firstValueFrom(await this.mistService.getAccessPointsDetails(this.formattedStoreNumber));
      const mist_details = detailsResponse?.['message'];
      main_response_data.forEach((mist: any) => {
        let foundMistDetails = false;
        if (mist_details != undefined) {
          mist_details.forEach((mist_detail: any) => {
            if (mist["mac"] === mist_detail["mac"]) {
              mist["lldp_port_id"] = mist_detail["lldp_port_id"];
              mist["lldp_system_name"] = mist_detail["lldp_system_name"];
              mist["eth0_port_speed"] = mist_detail["eth0_port_speed"];
              mist["uptime"] = mist_detail["uptime"];
              this.mists.push(mist);
              foundMistDetails = true;
            }
          });
          if (!foundMistDetails) {
            mist["lldp_port_id"] = "Unknown";
            mist["lldp_system_name"] = "Unknown";
            mist["eth0_port_speed"] = "Unknown";
            mist["uptime"] = "Unknown";
            this.mists.push(mist);
          }
        } else {
          mist["lldp_port_id"] = "Error";
          mist["lldp_system_name"] = "Error";
          mist["eth0_port_speed"] = "Error";
          mist["uptime"] = "Error";
          this.mists.push(mist);
        }
      });
    } catch (error) {
      console.log("Mists timed out!");
    }
  }

  
  async getCircuit() {
    try {
      const response = await firstValueFrom(await this.circuitService.getCircuitData(this.formattedStoreNumber));
      const response_data = response?.['message'];
      if (response_data != null) {
        let circuit: Circuit = response_data;
        this.circuits.push(circuit);
      }
    } catch (error) {
      console.log("Circuit timed out!");
    }
  }

  async getStoreDashboardLinkFromLogicMonitor() {
    try {
      const response = await firstValueFrom(await this.wanService.getStoreDashboardFromLogicMonitor(this.formattedStoreNumber));
      const link_response = response;
      this.storeDashboardLinkFromLogicMonitor = link_response["message"];
    } catch (error) {
      console.log("Dashboard link timed out!");
    }
  }
  getStoreDashboardLinkFromNetbox() {
    this.storeDashboardLinkFromNetbox = `${environment.netboxUrl}/dcim/sites/?slug=` + this.formattedStoreNumber
  }

  async getMistSiteLink() {
    try {
      const response = await firstValueFrom(await this.mistService.getSite(this.formattedStoreNumber));
      const site = response?.['message'] ?? response;
      if (site && site['org_id'] && site['id']) {
        this.mistSiteLink =
          `${environment.mistUrl}/admin/?org_id=${site['org_id']}#!dashboard/insights/site/${site['id']}`;
      }
    } catch (error) {
      console.log('Mist site link timed out!');
    }
  }

  async getMerakiDashboardUrls() {
    try {
      const response = await firstValueFrom(await this.mearkisService.getDevicesLatent(this.formattedStoreNumber));
      const devices_response = response;
      this.merakiDashboardUrls = devices_response["message"];
    } catch (error) {
      console.log("Meraki Dashboard link timed out!");
    }
  }

  setMerakiDashboardUrls() {
    this.merakiSwitchStatuses.forEach(switch_status => {
      switch_status.url = this.merakiDashboardUrls["devices"][switch_status.serial]["url"];
    });
  }

  combineNidWithImage() {
    const imageUrl = this.fiberNidImage && this.fiberNidImage["image"]
      ? this.fiberNidImage["image"]
      : null;
    if (!imageUrl && !this.fiberNidDescription && !this.circuitLocalLoop && !this.circuitCommitRate) {
      this.fiberNid = null;
      return;
    }

    this.fiberNid = {
      storeNumber: this.formattedStoreNumber,
      description: this.fiberNidDescription,
      imageUrl: imageUrl,
      localLoop: this.circuitLocalLoop,
      commitRate: this.circuitCommitRate
    }
  }

  resetView() {
    this.fortigates = [];
    this.cradlepoints = [];
    this.merakiSwitchStatuses = [];
    this.mists = [];
    this.expandedAps = {};
    this.allApsExpanded = false;
    this.location = [];
    this.circuits = [];
    this.mostRecentRemodels = [];
    this.networkState = null;
    this.networkStateEvaluation = null;
    this.activeVpnStatuses = {};
    this.vpnDataAvailable = true;
    this.wanInterfacesByHostname = {};
    this.wanInterfaceDataAvailable = true;
    this.storeDashboardLinkFromLogicMonitor = null;
    this.mistSiteLink = null;
    this.fiberNid = null;
    this.fiberNidImage = {};
    this.fiberNidDescription = null; 
    this.circuitLocalLoop = null;
    this.circuitCommitRate = null;
  }

  setMostRecentRemodel() {
    if (this.location[0]['locationStatus']['locationStatus'] == 'remodel') {
      this.getMostRecentRemodel();
    } else {
      if (this.location[0]['locationStatus']['tempCloseFlag'] && this.location[0]['locationStatus'].hasOwnProperty('tempCloseReason')) {
        if (this.location[0]['locationStatus']['tempCloseReason'].indexOf('remodel') > -1 || this.location[0]['locationStatus']['tempCloseReason'].indexOf('Remodel') > -1) {
          this.getMostRecentRemodel();
        }
      }
    }
  }

  getMostRecentRemodel() {
    /*
    Add remodels to the mostRecentRemodels list if elements 
    do not have a remodelReopenDate, but do have a remodelCloseDate
    */
    this.location[0]['remodelHistory'].forEach(remodelHistory => {
      if (!(remodelHistory.hasOwnProperty('remodelReopenDate')) && remodelHistory.hasOwnProperty('remodelCloseDate') && this.mostRecentRemodels.length < 1) {
        this.mostRecentRemodels.push(remodelHistory);
      }
    }); 
  }
}
